"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";

/*
 * The "this is what I read" table the research-statistics widgets show under a paste.
 *
 * Every word arrives through props, as with the kit primitives. Cells are strings the
 * widget has already formatted. Rows are positional — a pasted matrix has no ids — and
 * header cells may legitimately repeat ("N", "N"), so keys are positions throughout.
 */

export function PreviewTable({
  caption,
  head,
  rows,
  rowHeads,
  corner = "",
  limit = 8,
  more,
  flagged,
}: {
  caption?: string;
  head: readonly string[];
  rows: readonly (readonly string[])[];
  /** A label per row, drawn as a row header. */
  rowHeads?: readonly string[];
  /** Top-left cell when there are row headers. */
  corner?: string;
  /** Rows shown before the rest are summarised by `more`. */
  limit?: number;
  /** "… and {count} more rows", already pluralised by the widget. */
  more?: (hidden: number) => string;
  /** Row indexes to highlight (a row whose total is wrong, say). */
  flagged?: ReadonlySet<number>;
}) {
  const shown = rows.slice(0, limit);
  const hidden = rows.length - shown.length;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        {caption ? (
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {caption}
          </caption>
        ) : null}
        <thead>
          <tr>
            {rowHeads ? (
              <th className={TH} scope="col">
                {corner}
              </th>
            ) : null}
            {head.map((cell, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: header names may repeat
              <th className={TH} key={index} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, rowIndex) => (
            <tr
              className={
                flagged?.has(rowIndex)
                  ? "bg-destructive/5 text-destructive"
                  : ""
              }
              // biome-ignore lint/suspicious/noArrayIndexKey: positional data rows
              key={rowIndex}
            >
              {rowHeads ? (
                <th className={`${TD} font-medium`} scope="row">
                  {rowHeads[rowIndex] ?? ""}
                </th>
              ) : null}
              {row.map((cell, cellIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                <td className={`${TD} font-mono`} key={cellIndex}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && more ? (
        <p className="px-3 py-2 text-muted-foreground text-xs">
          {more(hidden)}
        </p>
      ) : null}
    </div>
  );
}
